import { useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import { FORM_CONSTANTS, VALIDATION_MESSAGES } from '../constants/formConstants';

export const useDraftManager = (id, isEditing, getValues, reset, currentStep) => {
  const [draftLoading, setDraftLoading] = useState(false);
  
  const DRAFT_KEY = `marketplace_publication_draft_${isEditing ? id : 'new'}`;

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  }, [DRAFT_KEY]);

  const saveDraft = useCallback(() => {
    if (isEditing) return;
    
    const formData = getValues();
    const draftData = {
      ...formData,
      currentStep,
      savedAt: new Date().toISOString(),
    };
    
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [getValues, currentStep, isEditing, DRAFT_KEY]);

  const loadDraft = useCallback(() => {
    if (isEditing) return Promise.resolve(1);
    
    try {
      const storedDraft = localStorage.getItem(DRAFT_KEY);
      
      if (storedDraft) {
        const storedData = JSON.parse(storedDraft);
        
        return new Promise((resolve) => {
          Swal.fire({
            title: VALIDATION_MESSAGES.DRAFT_FOUND,
            text: VALIDATION_MESSAGES.DRAFT_FOUND_QUESTION,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: VALIDATION_MESSAGES.CONFIRM_YES,
            cancelButtonText: VALIDATION_MESSAGES.CONFIRM_NO,
          }).then((result) => {
            if (result.isConfirmed) {
              // Create a clean copy of the data for reset
              const cleanData = { ...storedData };
              delete cleanData.currentStep;
              delete cleanData.savedAt;
              
              // Reset form with draft data
              reset(cleanData);
              
              resolve(storedData.currentStep || 1);
            } else {
              clearDraft();
              resolve(1);
            }
          });
        });
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
    
    return Promise.resolve(1);
  }, [isEditing, DRAFT_KEY, reset, clearDraft]);

  const handleSaveDraft = useCallback(async () => {
    if (isEditing) {
      Swal.fire({
        icon: 'info',
        title: 'Não disponível',
        text: VALIDATION_MESSAGES.DRAFT_NOT_AVAILABLE,
        timer: FORM_CONSTANTS.CONFIRMATION_TIMER,
      });
      return;
    }

    try {
      setDraftLoading(true);
      saveDraft();
      
      Swal.fire({
        icon: 'success',
        title: VALIDATION_MESSAGES.DRAFT_SAVED,
        text: VALIDATION_MESSAGES.DRAFT_SAVED_DESCRIPTION,
        timer: FORM_CONSTANTS.CONFIRMATION_TIMER,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error saving draft:', error);
      Swal.fire({
        icon: 'error',
        title: VALIDATION_MESSAGES.DRAFT_ERROR,
        text: VALIDATION_MESSAGES.DRAFT_ERROR_DESCRIPTION,
      });
    } finally {
      setDraftLoading(false);
    }
  }, [isEditing, saveDraft]);

  return {
    draftLoading,
    saveDraft,
    loadDraft,
    clearDraft,
    handleSaveDraft,
  };
};